import { configure, defineRule } from 'vee-validate'
import { localize, setLocale } from '@vee-validate/i18n'
import { messages as validationMessages } from '@/plugins/i18n'
import {
  required,
  max,
  min,
  email,
  length,
  numeric,
  min_value as minValue,
  size,
  mimes,
  one_of as oneOf
} from '@vee-validate/rules'

const regExp = {
  name: /^[A-ZĄĆĘŚŁŃÓŻŹ\s-–'’]*$/i,
  regularField: /^[A-ZĄĆĘŚŁŃÓŻŹ0-9\s!@#$%^&*+_:;?"~/,[\]()–'.-]*$/i,
  buildingOrFlatField: /^(?=.*\d)[a-zA-Z\d\-\/]{1,180}$/i,
  phone: /^[0-9]{9,21}$/,
  street: /^[A-ZĄĆĘŚŁŃÓŻŹ0-9\s-–.]*[A-ZĄĆĘŚŁŃÓŻŹ][A-ZĄĆĘŚŁŃÓŻŹ0-9\s-–.]*$/i,
  city: /^(?=.*\D)[\wĄĆĘŚŁŃÓŻŹ\s\d\-\/]+$/i,
  exclude: /[!#$%^&*()`/'"={};,ęóąśłżźćń]/i,
  email:
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  postCode: /^\d\d-\d\d\d$/,
  excludeSpecial: /^[^<>`{}=]*$/i,
  excludeSpecialWithCommaAndApostrophe: /^[^<>`{}=,\'"]*$/i,
  leastOneDigit: /^.*[0-9].*$/i,
  birthdate: /^[0-9]{2}\/[0-9]{2}\/[0-9]{4}$/i
}

configure({
  generateMessage: localize({
    pl: {
      messages: {
        ...validationMessages.pl.validation
      }
    }
  })
})
setLocale('pl')

defineRule('required', required)
defineRule('email', email)
defineRule('max', max)
defineRule('min', min)
defineRule('length', length)
defineRule('numeric', numeric)
defineRule('min_value', minValue)
defineRule('size', size)
defineRule('mimes', mimes)
defineRule('oneOf', oneOf)

defineRule('exclude_special', (value: string) => !value || regExp.excludeSpecial.test(value))

defineRule('exclude_special_with_comma_and_apostrophe', (value: string) =>
  regExp.excludeSpecialWithCommaAndApostrophe.test(value)
)

defineRule('booleanRequired', (value: unknown) => typeof value === 'boolean')
defineRule('least_one_digit', (value: string) => !value || regExp.leastOneDigit.test(value))

defineRule('birthdate', (value: string) => regExp.birthdate.test(value))

const isValidDate = (year: string, month: string, day: string) => {
  const d = new Date(Number(year), Number(month) - 1, Number(day))
  return (
    d.getFullYear() === Number(year) &&
    d.getMonth() === Number(month) - 1 &&
    d.getDate() === Number(day)
  )
}

defineRule('date_exists', (value: string) => {
  if (!value) {
    return false
  }

  const [day, month, year] = value.split('/')
  if (!day || !month || !year) {
    return false
  }

  return isValidDate(year, month, day)
})

const isValidPostalCode = (value: string) => value?.length === 6
defineRule('postal_code_length', isValidPostalCode)

defineRule('postalCode', isValidPostalCode)

defineRule('name', (value: string) => regExp.name.test(value))

defineRule('lastName', (value: string) => regExp.name.test(value))

defineRule('street', (value: string) => regExp.street.test(value))

defineRule('regularField', (value: string) => regExp.regularField.test(value))

defineRule(
  'buildingOrFlatField',
  (value: string) => !value || regExp.buildingOrFlatField.test(value)
)

defineRule('phoneNumber', (value: string) => {
  if (!value) return true
  const trimmed = value.replace(/\s+/g, '')
  return regExp.phone.test(trimmed)
})

defineRule('companyName', (value: string) => regExp.regularField.test(value))

defineRule('city', (value: string) => regExp.city.test(value))

const validateImage = (
  file: File,
  width: number,
  height: number,
  condition: string
): Promise<boolean> => {
  return new Promise((resolve) => {
    const image = new Image()
    image.onload = () => {
      const conditions: { [key: string]: boolean } = {
        minDimensionsWithoutRotation: image.width >= width && image.height >= height,
        minDimensionsWithRotation:
          (image.width >= width && image.height >= height) ||
          (image.width >= height && image.height >= width)
        // maxDimensions or exactDimension may be added here
      }
      resolve(conditions[condition])
    }
    image.src = URL.createObjectURL(file)
  })
}

const validateImages = (
  files: File[],
  width: number,
  height: number,
  condition: string
): Promise<boolean> => {
  const list: File[] = []
  const fileArray = Array.isArray(files) ? files : [files]
  for (let i = 0; i < fileArray.length; i += 1) {
    // if file is not an image, reject.
    if (!/\.(jpg|svg|jpeg|png|bmp|gif)$/i.test(fileArray[i]?.name)) {
      return Promise.resolve(false)
    }
    list.push(fileArray[i])
  }
  return Promise.all(list.map((file) => validateImage(file, width, height, condition))).then(
    (values) => values.every((v) => v)
  )
}

defineRule('minDimensionsWithoutRotation', (files: File[], params: [number, number]) => {
  const [width, height] = params
  return validateImages(files, width, height, 'minDimensionsWithoutRotation')
})

defineRule('minDimensionsWithRotation', (files: File[], params: [number, number]) => {
  const [width, height] = params
  return validateImages(files, width, height, 'minDimensionsWithRotation')
})

defineRule('not_same_child', (value: number, [currentId]: [number]) => {
  if (!currentId || !value) return true
  return value !== currentId
})
